import type { Locale } from "@/stores/i18nStore";
import { getCollection, getCollectionCopy } from "@/lib/merchandising";

export const SITE_ORIGIN = "https://skinvault.be";

type SeoSettingsLike = {
  pageTitle?: string;
  metaDescription?: string;
  ogImage?: string;
};

export type PageSeo = {
  title: string;
  description: string;
  canonical: string;
  robots: "index,follow" | "noindex,nofollow";
  ogImage?: string;
};

const LEGACY_TITLES = new Set([
  "SkinVault — Premium 3M Vinyl Skins",
  "SkinVault - Premium 3M Vinyl Skins",
]);

const LEGACY_DESCRIPTIONS = new Set([
  "Transform your devices with premium 3M vinyl skins.",
  "Transform your devices with premium 3M vinyl skins. Scratch-proof, bubble-free, ultra-thin protection for iPhones, MacBooks, cameras, and more.",
]);

const COPY = {
  fr: {
    homeTitle: "SkinVault | Skins premium pour vos appareils",
    homeDescription: "Skins Hydrogel et vinyle 3M sur mesure pour téléphones, ordinateurs, tablettes, gaming et appareils photo. Découpe précise en Belgique.",
    catalogTitle: "Catalogue de skins pour appareils | SkinVault",
    catalogDescription: "Parcourez les skins SkinVault par appareil ou par collection et trouvez rapidement un design compatible.",
    categoryDescription: (label: string) => `Découvrez nos ${label.toLowerCase()} découpés sur mesure, préparés en Belgique et expédiés rapidement.`,
    collectionDescription: (label: string) => `Découvrez la collection ${label} de SkinVault et choisissez le design adapté à votre appareil.`,
    productDescription: "Découvrez ce skin premium SkinVault, sélectionnez votre modèle et commandez une découpe sur mesure.",
  },
  en: {
    homeTitle: "SkinVault | Premium skins for your devices",
    homeDescription: "Custom Hydrogel and 3M vinyl skins for phones, laptops, tablets, gaming devices and cameras, precisely cut in Belgium.",
    catalogTitle: "Device skin catalogue | SkinVault",
    catalogDescription: "Browse SkinVault skins by device or collection and quickly find a compatible design.",
    categoryDescription: (label: string) => `Explore custom-cut ${label.toLowerCase()} prepared in Belgium and shipped quickly.`,
    collectionDescription: (label: string) => `Explore SkinVault's ${label} collection and choose a design for your device.`,
    productDescription: "Discover this premium SkinVault skin, select your model and order a precise custom cut.",
  },
} as const;

const CATEGORY_LABELS: Record<string, Record<Locale, string>> = {
  phones: { fr: "Skins pour téléphones", en: "Phone skins", nl: "Telefoonskins", pl: "Skiny na telefony", ro: "Skin-uri pentru telefoane" },
  macbooks: { fr: "Skins pour ordinateurs portables", en: "Laptop skins", nl: "Laptopskins", pl: "Skiny na laptopy", ro: "Skin-uri pentru laptopuri" },
  laptops: { fr: "Skins pour ordinateurs portables", en: "Laptop skins", nl: "Laptopskins", pl: "Skiny na laptopy", ro: "Skin-uri pentru laptopuri" },
  tablets: { fr: "Skins pour tablettes", en: "Tablet skins", nl: "Tabletskins", pl: "Skiny na tablety", ro: "Skin-uri pentru tablete" },
  cameras: { fr: "Skins pour appareils photo", en: "Camera skins", nl: "Cameraskins", pl: "Skiny na aparaty", ro: "Skin-uri pentru camere" },
  consoles: { fr: "Skins pour consoles et manettes", en: "Gaming skins", nl: "Gaming skins", pl: "Skiny gamingowe", ro: "Skin-uri gaming" },
  cards: { fr: "Skins pour cartes", en: "Card skins", nl: "Kaartskins", pl: "Skiny na karty", ro: "Skin-uri pentru carduri" },
  accessories: { fr: "Protection et accessoires", en: "Protection and accessories", nl: "Bescherming en accessoires", pl: "Ochrona i akcesoria", ro: "Protecție și accesorii" },
};

const CATEGORY_ALIASES: Record<string, string> = {
  gaming: "consoles",
  controllers: "consoles",
  "card-skins": "cards",
};

const localCopy = (locale: Locale) => (locale === "fr" ? COPY.fr : COPY.en);

const titleCase = (value: string) =>
  decodeURIComponent(value)
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const canonicalPath = (pathname: string) => {
  const raw = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const categoryMatch = raw.match(/^\/category\/([^/]+)\/?$/);
  if (categoryMatch) {
    const canonicalSlug = CATEGORY_ALIASES[categoryMatch[1]] || categoryMatch[1];
    return `/category/${canonicalSlug}`;
  }
  return raw === "/" ? "/" : raw.replace(/\/+$/, "");
};

export function buildPageSeo(
  pathname: string,
  locale: Locale,
  settingsSeo?: SeoSettingsLike,
): PageSeo {
  const copy = localCopy(locale);
  const path = canonicalPath(pathname);
  const parts = path.split("/").filter(Boolean);
  let title: string = copy.homeTitle;
  let description: string = copy.homeDescription;

  if (path === "/") {
    const configuredTitle = settingsSeo?.pageTitle?.trim();
    const configuredDescription = settingsSeo?.metaDescription?.trim();
    if (configuredTitle && !LEGACY_TITLES.has(configuredTitle)) title = configuredTitle;
    if (configuredDescription && !LEGACY_DESCRIPTIONS.has(configuredDescription)) {
      description = configuredDescription;
    }
  } else if (path === "/catalog") {
    title = copy.catalogTitle;
    description = copy.catalogDescription;
  } else if (parts[0] === "category" && parts[1]) {
    const label = CATEGORY_LABELS[parts[1]]?.[locale] || CATEGORY_LABELS[parts[1]]?.en || titleCase(parts[1]);
    title = `${label} | SkinVault`;
    description = copy.categoryDescription(label);
  } else if (parts[0] === "collection" && parts[1]) {
    const collection = getCollection(parts[1]);
    const label = collection ? getCollectionCopy(collection, locale).label : titleCase(parts[1]);
    title = `${label} | Collection SkinVault`;
    description = collection
      ? getCollectionCopy(collection, locale).description
      : copy.collectionDescription(label);
  } else if (parts[0] === "product" && parts[1]) {
    const productName = titleCase(parts.slice(1).join("-"));
    title = `${productName} | SkinVault`;
    description = copy.productDescription;
  } else {
    title = `${titleCase(parts[0] || "SkinVault")} | SkinVault`;
  }

  const privateRoute = ["admin", "auth", "profile", "reset-password", "unsubscribe"].includes(parts[0] || "");

  return {
    title,
    description,
    canonical: `${SITE_ORIGIN}${path}`,
    robots: privateRoute ? "noindex,nofollow" : "index,follow",
    ogImage: settingsSeo?.ogImage || undefined,
  };
}
